/* ============================================================================
   milano-quickview.js — Quick view for the collection grid (v1, 14 Oct 2026)
   Loaded (deferred) by sections/milano-quickview.liquid on in-scope collection pages only.

   Jobs
     1. Put a "Quick view" button on every Searchanise card (li.snize-product) — and on any
        element carrying data-mw-qv-card — and keep doing so when Searchanise re-renders
        (filters, sort, paging): MutationObserver + Searchanise's own jQuery events.
     2. Open a native <dialog> with the product rendered by the Section Rendering API
        (sections/api-milano-quickview.liquid), cached per product and prefetched on hover/focus.
     3. Drive the panel: gallery (scroll-snap, arrows, dots, thumbs), option pickers → variant
        (price node toggle, availability, Add-to-cart state, PDP links, variant image), "More shades"
        swaps, add to cart through the theme's <product-form>, close on Esc / backdrop / drag-down /
        theme:cart:added, focus return.

   Invariants kept: no price HTML is written here (Discount Ninja owns prices — each variant has
   its own server-rendered node; this file only toggles `hidden`); the add-to-cart block for
   wigs/toppers is REMOVED from the fragment before it enters the DOM unless the editor setting
   allows it; the DN Searchanise event (la:dn:collection:updated) is published after each render.
   No dependencies. ES2017. Progressive: browsers without <dialog>.showModal get no button.
   ============================================================================ */
(function () {
  'use strict';

  if (window.MilanoQuickview) return;

  var dialog = document.querySelector('dialog[data-mw-qv]');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  var cfg = {};
  try {
    var cfgEl = document.querySelector('[data-mw-qv-config]');
    cfg = cfgEl ? JSON.parse(cfgEl.textContent) : {};
  } catch (e) { cfg = {}; }
  var S = cfg.strings || {};
  var SECTION = cfg.section || 'api-milano-quickview';
  var CARD = '.snize-product, [data-mw-qv-card]';
  var EXCLUDE = '.snize-recommendation, [class*="recommendation"], .snize-ac-results';
  var PREFETCH_DELAY = 150;
  var MAX_CACHE = 40;

  var html = document.documentElement;
  var panel = dialog.querySelector('[data-qv-panel]');
  var body = dialog.querySelector('[data-qv-body]');
  var foot = dialog.querySelector('[data-qv-foot]');
  var skeleton = body.querySelector('[data-qv-skeleton]');
  var status = dialog.querySelector('[data-qv-status]');   // 26 Oct: live region outside the aria-busy body
  var closeBtn = dialog.querySelector('[data-qv-close]');
  var handle = dialog.querySelector('[data-qv-handle]');
  var note = foot ? foot.querySelector('[data-qv-note]') : null;
  var navPrev = dialog.querySelector('[data-qv-prev-product]');
  var navNext = dialog.querySelector('[data-qv-next-product]');
  var currentCard = null;           // the grid card the open panel belongs to (prev/next browse it)
  var fineHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  var cache = new Map();          // sectionUrl -> Promise<DocumentFragment source string>
  var cacheOrder = [];
  var inflight = 0;
  var prefetchQueue = [];
  var lastTrigger = null;
  var openSeq = 0;
  var current = null;             // { root, variants, options:[fieldset], stage, slides, ... }
  var scrollYAtOpen = 0;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var coarse = window.matchMedia('(hover: none), (pointer: coarse)').matches;

  if (cfg.triggerMobile === 'label') html.classList.add('mw-qv-mobile-label');
  if (cfg.triggerMobile === 'hidden') html.classList.add('mw-qv-mobile-hidden');

  /* ------------------------------------------------------------------ utils */
  function debounce(fn, ms) {
    var t = null;
    return function () {
      var args = arguments;
      if (t) clearTimeout(t);
      t = setTimeout(function () { t = null; fn.apply(null, args); }, ms);
    };
  }
  // 2026-10-22 INP: cached media query instead of an innerWidth read (which can force layout mid-handler).
  var phoneMq = window.matchMedia('(max-width: 767px)');
  function isPhone() { return phoneMq.matches; }
  // 2026-10-22 INP: resolve after the next paint (rAF, then a task), so work queued behind it never delays
  // the frame that shows a tap's feedback. The timeout covers hidden tabs, where rAF does not run.
  function afterPaint() {
    return new Promise(function (resolve) {
      var done = false;
      var go = function () { if (!done) { done = true; resolve(); } };
      requestAnimationFrame(function () { setTimeout(go, 0); });
      setTimeout(go, 120);
    });
  }
  function rootPath() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.slice(-1) === '/' ? r : r + '/';
  }
  function productInfoFromCard(card) {
    var links = card.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i += 1) {
      var href = links[i].getAttribute('href');
      if (!href) continue;
      try {
        var u = new URL(href, window.location.origin);
        var m = u.pathname.match(/\/products\/([^/?#]+)/i);
        if (!m) continue;
        var variant = u.searchParams.get('variant') || '';
        if (!variant) {
          var fixed = card.getAttribute('data-snize-variant-fixed') || '';
          var fm = fixed.match(/(\d{6,})/);
          if (fm) variant = fm[1];
        }
        var title = card.getAttribute('data-mw-qv-title') || '';
        if (!title) {
          var t = card.querySelector('.snize-title, [data-mw-qv-title-node]');
          title = t ? t.textContent.trim() : (links[i].getAttribute('aria-label') || '');
        }
        return { path: u.pathname, variant: variant, title: title };
      } catch (e) { /* next link */ }
    }
    return null;
  }
  function sectionUrl(path, variant) {
    var url = path + '?section_id=' + encodeURIComponent(SECTION);
    if (variant) url += '&variant=' + encodeURIComponent(variant);
    return url;
  }
  function cacheSet(key, promise) {
    cache.set(key, promise);
    cacheOrder.push(key);
    while (cacheOrder.length > MAX_CACHE) {
      var old = cacheOrder.shift();
      if (old !== key) cache.delete(old);
    }
  }
  var FETCH_TIMEOUT = 10000;   // 26 Oct: a hung request used to leave the skeleton up forever
  function load(url) {
    if (cache.has(url)) return cache.get(url);
    inflight += 1;
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, FETCH_TIMEOUT) : null;
    var p = fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' }, signal: ctrl ? ctrl.signal : undefined })
      .then(function (r) { if (timer) clearTimeout(timer); if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); }, function (err) { if (timer) clearTimeout(timer); throw err; })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var content = doc.querySelector('[data-qv-content]');
        if (!content) throw new Error('No quick view content');
        return content.outerHTML;
      });
    p.then(function () { inflight -= 1; drainPrefetch(); }, function () {
      inflight -= 1; cache.delete(url); drainPrefetch();
    });
    cacheSet(url, p);
    return p;
  }

  /* ------------------------------------------------------------- prefetch */
  function drainPrefetch() {
    while (inflight < 2 && prefetchQueue.length) {
      var url = prefetchQueue.shift();
      if (!cache.has(url)) load(url).catch(function () {});
    }
  }
  function prefetch(info) {
    if (cfg.prefetch === false || !info) return;
    var url = sectionUrl(info.path, info.variant);
    if (cache.has(url) || prefetchQueue.indexOf(url) !== -1) return;
    prefetchQueue.push(url);
    drainPrefetch();
  }
  var hoverTimer = null;
  var hoverCard = null;
  document.addEventListener('pointerover', function (e) {
    if (coarse) return;
    var card = e.target.closest && e.target.closest(CARD);
    if (!card || card === hoverCard || !card.hasAttribute('data-mw-qv-ready')) return;
    hoverCard = card;
    if (hoverTimer) clearTimeout(hoverTimer);
    hoverTimer = setTimeout(function () { prefetch(productInfoFromCard(card)); }, PREFETCH_DELAY);
  });
  document.addEventListener('pointerout', function (e) {
    if (!hoverCard) return;
    var to = e.relatedTarget;
    if (to && hoverCard.contains(to)) return;
    hoverCard = null;
    if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null; }
  });
  document.addEventListener('focusin', function (e) {
    var btn = e.target.closest && e.target.closest('.mw-qv-trigger');
    if (btn) prefetch(productInfoFromCard(btn.parentNode));
  });

  /* ------------------------------------------------------------- triggers */
  var ICON = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M1.8 10S4.8 4.5 10 4.5 18.2 10 18.2 10 15.2 15.5 10 15.5 1.8 10 1.8 10z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><circle cx="10" cy="10" r="2.6" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';
  var placeObserver = null;

  // 2026-10-22 INP: placement is batched (all reads, then all writes). Reading offsetTop right after each
  // card's button was appended forced one full-page layout per card (24 per grid render / filter / sort).
  function placeTriggers(cards) {
    var tops = cards.map(function (card) {
      var wrap = card.querySelector('.snize-thumbnail-wrapper, [data-mw-qv-media], .snize-thumbnail, img');
      return wrap ? wrap.offsetTop + wrap.offsetHeight : 0;
    });
    cards.forEach(function (card, i) {
      var v = tops[i] > 0 ? tops[i] + 'px' : '';
      if (v && card.style.getPropertyValue('--mw-qv-top') !== v) card.style.setProperty('--mw-qv-top', v);
    });
  }
  function placeTrigger(card) { placeTriggers([card]); }
  function enhanceCard(card) {
    // 26 Oct: also re-add the button if the card kept its marker but lost the button (innerHTML-only re-render).
    if (card.closest(EXCLUDE)) return null;
    if (card.hasAttribute('data-mw-qv-ready') && card.querySelector(':scope > .mw-qv-trigger')) return null;
    var info = productInfoFromCard(card);
    if (!info) return null;
    card.setAttribute('data-mw-qv-ready', '');
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mw-qv-trigger';
    btn.setAttribute('aria-label', (S.quickViewOf || 'Quick view:') + ' ' + info.title);
    btn.setAttribute('aria-haspopup', 'dialog');
    btn.innerHTML = ICON + '<span class="mw-qv-trigger__label">' + escapeHtml(cfg.triggerLabel || 'Quick view') + '</span>';
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      open(productInfoFromCard(card) || info, btn);
    });
    card.appendChild(btn);
    if (!placeObserver && 'ResizeObserver' in window) {
      placeObserver = new ResizeObserver(function (entries) {
        var cards = [];
        entries.forEach(function (en) {
          var c = en.target.closest(CARD);
          if (c && cards.indexOf(c) === -1) cards.push(c);
        });
        placeTriggers(cards);
      });
    }
    var wrap = card.querySelector('.snize-thumbnail-wrapper, [data-mw-qv-media]');
    if (placeObserver && wrap) placeObserver.observe(wrap);
    return card;
  }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  var scan = debounce(function () {
    var added = [];
    document.querySelectorAll(CARD).forEach(function (card) { var c = enhanceCard(card); if (c) added.push(c); });
    if (added.length) placeTriggers(added);
  }, 100);
  function nodeHasCard(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.matches && node.matches(CARD)) return true;
    return !!(node.querySelector && node.querySelector(CARD));
  }
  function observeCards() {
    if (!document.body) return;
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i += 1) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j += 1) {
          if (nodeHasCard(added[j])) { scan(); return; }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }
  var jqBound = false;
  function bindSearchanise() {
    if (jqBound) return;
    try {
      if (!window.Searchanise || typeof window.Searchanise.$ !== 'function') return;
      jqBound = true;
      window.Searchanise.$(document).on('Searchanise.ResultsUpdated', scan);
    } catch (e) { /* no-op */ }
  }
  document.addEventListener('Searchanise.Loaded', function () { bindSearchanise(); scan(); });
  document.addEventListener('Searchanise.ResultsUpdated', scan);
  window.addEventListener('resize', debounce(function () {
    placeTriggers(Array.prototype.slice.call(document.querySelectorAll(CARD + '[data-mw-qv-ready]')));
    if (dialog.open) { navList = gridCards(); updateNav(); if (current && current.root) { initShades(current.root); initInfoFade(current.root); } }
  }, 150));
  window.addEventListener('pageshow', function (e) { if (e.persisted) scan(); });

  function boot() {
    observeCards();
    bindSearchanise();
    scan();
    [400, 1200, 3000, 6000].forEach(function (ms) {
      setTimeout(function () { bindSearchanise(); scan(); }, ms);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  /* ----------------------------------------------------------- open/close */
  function lock(y) {
    scrollYAtOpen = y;
    html.classList.add('mw-qv-lock');
    if (coarse) {
      document.body.style.position = 'fixed';
      document.body.style.top = -scrollYAtOpen + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }
  }
  function unlock() {
    html.classList.remove('mw-qv-lock');
    if (coarse) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollYAtOpen);
    }
  }
  function setStatus(text) { if (status) status.textContent = text || ''; }
  function showSkeleton() {
    parkFoot();
    body.querySelectorAll('[data-qv-content], .mw-qv__error').forEach(function (n) { n.remove(); });
    if (skeleton) skeleton.hidden = false;
    body.setAttribute('aria-busy', 'true');
    body.classList.remove('is-swapping');
    setStatus(S.loading || 'Loading');
  }
  // 2026-10-22 INP: no offsetParent (a tap leaves style dirty, so the first read forced a full-page style +
  // layout pass inside the click handler). Recommendation widgets are excluded by EXCLUDE; anything else
  // hidden sits under a [hidden] ancestor or an inline display:none.
  function gridCards() {
    return Array.prototype.filter.call(document.querySelectorAll(CARD + '[data-mw-qv-ready]'), function (c) {
      return c.isConnected && !c.closest(EXCLUDE + ', [hidden], [style*="display: none"], [style*="display:none"]');
    });
  }
  // 2026-10-22 INP: the visible-card list is read ONCE per open (before any DOM write, while layout is still
  // clean) and reused by neighbour()/updateNav(); it used to be rebuilt 5–8x per open, each time forcing layout.
  var navList = null;
  function neighbour(delta) {
    if (!currentCard) return null;
    var list = navList || (navList = gridCards());
    var i = list.indexOf(currentCard);
    if (i === -1) return null;
    return list[i + delta] || null;
  }
  function updateNav() {
    if (!navPrev || !navNext) return;
    var hasCard = !!currentCard && (navList || (navList = gridCards())).indexOf(currentCard) !== -1;
    navPrev.disabled = !hasCard || !neighbour(-1);
    navNext.disabled = !hasCard || !neighbour(1);
    var nav = navPrev.parentNode;
    if (nav) nav.hidden = cfg.prevNext === false || !hasCard;
  }
  function openCard(card) {
    if (!card) return;
    var info = productInfoFromCard(card);
    if (!info) return;
    open(info, card.querySelector('.mw-qv-trigger'));
  }
  function open(info, trigger) {
    if (!info) return;
    // 2026-10-22 INP: every layout read happens here, before the first write.
    var y = window.scrollY || window.pageYOffset || 0;
    navList = gridCards();
    lastTrigger = trigger || null;
    currentCard = trigger && trigger.closest ? trigger.closest(CARD) : null;
    updateNav();
    if (trigger) trigger.classList.add('is-loading');
    var seq = ++openSeq;
    var url = sectionUrl(info.path, info.variant);
    current = null;
    var needLock = false;
    if (!dialog.open) {
      try { dialog.showModal(); } catch (e) { window.location.href = info.path; return; }
      openedAt = performance.now();
      needLock = true;
      dialog.classList.remove('is-closing');
      requestAnimationFrame(function () { requestAnimationFrame(function () { dialog.classList.add('is-open'); }); });
    }
    showSkeleton();
    var tStart = performance.now();
    var fetching = load(url);   // the request starts now; only the DOM work waits for the paint
    // The tap ends with the panel + skeleton on screen. The scroll lock (a full-page relayout on phones),
    // the render and the neighbour prefetch run after that paint, so they no longer count toward INP.
    afterPaint().then(function () {
      if (seq !== openSeq) return null;
      if (needLock && dialog.open) lock(y);
      body.scrollTop = 0;
      return fetching.then(function (markup) {
        if (seq !== openSeq) return;
        render(markup, info);
        announce('open', info, performance.now() - tStart);
        prefetchNeighbours();
      });
    }).catch(function () {
      if (seq !== openSeq) return;
      renderError(info);
    }).then(function () {
      if (trigger) trigger.classList.remove('is-loading');
    });
  }
  // Neighbours are only browsed with the prev/next arrows, which phones hide (see the 21 Oct CSS block).
  function prefetchNeighbours() {
    if (!currentCard || cfg.prevNext === false || isPhone()) return;
    [-1, 1].forEach(function (d) { var n = neighbour(d); if (n) prefetch(productInfoFromCard(n)); });
  }
  function parkFoot() {
    // The trust row + note live in the panel shell and are moved into each product's
    // [data-qv-foot-slot] / [data-qv-note-slot]; bring them home before that content is thrown away.
    if (!foot) return;
    if (note && note.parentNode !== foot) foot.appendChild(note);
    if (foot.parentNode !== panel) panel.appendChild(foot);
    foot.style.display = '';
  }
  function close(opts) {
    if (!dialog.open) return;
    opts = opts || {};
    openSeq += 1;
    panel.style.transform = '';   // 26 Oct: a drag's inline transform used to beat .is-closing (no slide-out)
    dialog.classList.add('is-closing');
    dialog.classList.remove('is-open');
    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      try { dialog.close(); } catch (e) { /* already closed */ }
      dialog.classList.remove('is-closing', 'is-dragging');
      panel.style.transform = '';
      unlock();
      parkFoot();
      body.querySelectorAll('[data-qv-content], .mw-qv__error').forEach(function (n) { n.remove(); });
      current = null;
      currentCard = null;
      navList = null;
      if (opts.restoreFocus !== false && lastTrigger && document.contains(lastTrigger)) {
        // 26 Oct: after prev/next browsing the trigger can be off-screen; let focus scroll it into view then.
        var r = lastTrigger.getBoundingClientRect();
        var onScreen = r.bottom > 0 && r.top < (window.innerHeight || document.documentElement.clientHeight);
        try { lastTrigger.focus({ preventScroll: onScreen }); } catch (e) { lastTrigger.focus(); }
      }
      document.dispatchEvent(new CustomEvent('mw:quickview:closed', { bubbles: true }));
    };
    if (opts.immediate) finish(); else setTimeout(finish, reduced ? 0 : 260);
  }
  dialog.addEventListener('cancel', function (e) { e.preventDefault(); close(); });
  // Escape is ours while the panel is open: the native cancel event closes it; the keystroke must
  // not reach the theme's or Searchanise's document-level Escape handlers behind the modal.
  ['keydown', 'keyup'].forEach(function (type) {
    dialog.addEventListener(type, function (e) { if (e.key === 'Escape' || e.key === 'Esc') e.stopPropagation(); });
  });
  dialog.addEventListener('close', function () {
    // Closed by something else (e.g. another script): keep state consistent.
    if (html.classList.contains('mw-qv-lock')) { unlock(); dialog.classList.remove('is-open', 'is-closing'); parkFoot(); }
  });
  // 22 Oct P2: the backdrop closes only for a deliberate tap on it: the press must also start on the backdrop
  // (a text selection released outside the panel no longer closes it) and not within 350 ms of opening (the
  // second tap of a double-tap on a card trigger used to land on the fresh backdrop and close the panel at once).
  var openedAt = 0, downOnBackdrop = false;
  dialog.addEventListener('pointerdown', function (e) { downOnBackdrop = e.target === dialog; });
  dialog.addEventListener('click', function (e) {
    if (e.target !== dialog || !downOnBackdrop) return;
    if (performance.now() - openedAt < 350) return;
    close();
  });
  closeBtn.addEventListener('click', function () { close(); });
  if (navPrev) navPrev.addEventListener('click', function () { openCard(neighbour(-1)); });
  if (navNext) navNext.addEventListener('click', function () { openCard(neighbour(1)); });
  // The theme's cart drawer opens itself after an add; hand the page (and focus) over to it.
  document.addEventListener('theme:cart:added', function () { if (dialog.open) close({ restoreFocus: false, immediate: true }); });
  document.addEventListener('theme:cart:open', function () { if (dialog.open) close({ restoreFocus: false, immediate: true }); });

  /* -------------------------------------------------------- drag to close */
  (function dragToClose() {
    var startY = 0, lastY = 0, lastT = 0, dragging = false, pointerId = null;
    function onDown(e) {
      if (!isPhone() || e.pointerType === 'mouse' && e.target !== handle) return;
      var scroller = body;
      var fromHandle = handle && handle.contains(e.target);
      if (!fromHandle && scroller.scrollTop > 0) return;
      if (!fromHandle && e.target.closest('.mw-qv__stage, .mw-qv__shade-list, button, a, input, label')) return;
      dragging = true; pointerId = e.pointerId; startY = lastY = e.clientY; lastT = e.timeStamp;
      dialog.classList.add('is-dragging');
    }
    function onMove(e) {
      if (!dragging || e.pointerId !== pointerId) return;
      var dy = Math.max(0, e.clientY - startY);
      lastY = e.clientY; lastT = e.timeStamp;
      panel.style.transform = 'translateY(' + dy + 'px)';
    }
    function onUp(e) {
      if (!dragging || e.pointerId !== pointerId) return;
      dragging = false;
      dialog.classList.remove('is-dragging');
      var dy = Math.max(0, e.clientY - startY);
      var dt = Math.max(1, e.timeStamp - lastT);
      var velocity = (e.clientY - lastY) / dt;
      if (dy > 110 || velocity > 0.6) { close(); } else { panel.style.transform = ''; }
    }
    panel.addEventListener('pointerdown', onDown);
    panel.addEventListener('pointermove', onMove, { passive: true });
    panel.addEventListener('pointerup', onUp);
    panel.addEventListener('pointercancel', onUp);
  })();

  /* ---------------------------------------------------------------- render */
  function atcAllowed(root) {
    var mode = cfg.atcMode || 'accessories';
    if (mode === 'none') return false;
    if (mode === 'all') return true;
    return root.getAttribute('data-qv-accessory') === 'true';
  }
  function prune(root) {
    var atc = root.querySelector('[data-qv-atc]');
    var pdp = root.querySelector('[data-qv-pdp]');
    if (atcAllowed(root)) { if (pdp) pdp.remove(); } else if (atc) { atc.remove(); }
    var pdpLabel = root.querySelector('[data-qv-pdp-label]');
    if (pdpLabel && cfg.pdpLabel) pdpLabel.textContent = cfg.pdpLabel;
    var detailsLabel = root.querySelector('[data-qv-details-label]');
    if (detailsLabel && cfg.detailsLabel) detailsLabel.textContent = cfg.detailsLabel;
    if (cfg.showFinance === false) root.querySelectorAll('[data-qv-finance]').forEach(function (n) { n.remove(); });
    var shades = root.querySelector('[data-qv-shades]');
    if (shades) {
      if (cfg.showShades === false) { shades.remove(); } else {
        var limit = parseInt(cfg.shadesLimit, 10) || 8;
        shades.querySelectorAll('[data-qv-shade-item]').forEach(function (li, i) { if (i >= limit) li.hidden = true; });
      }
    }
    var slot = root.querySelector('[data-qv-foot-slot]');
    if (slot && foot) {
      slot.appendChild(foot);
      foot.style.display = 'block';
    }
    if (note) {
      var noteSlot = root.querySelector('[data-qv-note-slot]');
      var showNote = !root.querySelector('[data-qv-atc]');
      note.hidden = !showNote;
      if (showNote && noteSlot) noteSlot.appendChild(note); else if (foot) foot.appendChild(note);
    }
  }
  function render(markup, info) {
    if (body.scrollTop) body.scrollTop = 0;   // before the DOM writes below, while layout is clean
    var tpl = document.createElement('template');
    tpl.innerHTML = markup.trim();
    var root = tpl.content.querySelector('[data-qv-content]');
    if (!root) { renderError(info); return; }
    prune(root);
    body.querySelectorAll('[data-qv-content], .mw-qv__error').forEach(function (n) { n.remove(); });
    if (skeleton) skeleton.hidden = true;
    body.appendChild(root);
    body.classList.remove('is-swapping');
    body.setAttribute('aria-busy', 'false');
    setStatus('');
    initContent(root);
    var title = root.querySelector('[data-qv-title]');
    if (title) {
      var active = document.activeElement;
      // 26 Oct: also when focus sits on a prev/next button that just became disabled (grid end).
      if (!active || active === closeBtn || active === dialog || !dialog.contains(active) || active.disabled) {
        try { title.focus({ preventScroll: true }); } catch (e) { title.focus(); }
      }
    }
    requestAnimationFrame(function () {
      document.dispatchEvent(new CustomEvent('la:dn:collection:updated'));
      // 26 Oct: removed the okeWidgetApi.initWidget call. The fragment prints no [data-oke-widget]; its stars are
      // Okendo's server-rendered StarRatingSnippet.
    });
  }
  function renderError(info) {
    body.querySelectorAll('[data-qv-content], .mw-qv__error').forEach(function (n) { n.remove(); });
    if (skeleton) skeleton.hidden = true;
    body.classList.remove('is-swapping');
    body.setAttribute('aria-busy', 'false');
    var box = document.createElement('div');
    box.className = 'mw-qv__error';
    box.innerHTML = '<p>' + escapeHtml(S.error || 'This product could not be loaded here.') + '</p>' +
      '<a class="mw-qv__btn mw-qv__btn--primary" href="' + escapeHtml(info.path) + '">' + escapeHtml(S.errorLink || 'Open the product page') + '</a>';
    body.appendChild(box);
    setStatus(S.error || '');
  }
  function announce(kind, info, ms) {
    try {
      document.dispatchEvent(new CustomEvent('mw:quickview:' + kind, { bubbles: true, detail: { path: info.path, variant: info.variant, ms: Math.round(ms || 0) } }));
      if (typeof window.clarity === 'function') window.clarity('event', 'Quick view opened');
    } catch (e) { /* analytics never breaks the UI */ }
  }

  /* --------------------------------------------------------- panel logic */
  function initContent(root) {
    var variants = [];
    try {
      var vEl = root.querySelector('[data-qv-variants]');
      variants = vEl ? JSON.parse(vEl.textContent) : [];
    } catch (e) { variants = []; }
    current = {
      root: root,
      path: root.getAttribute('data-qv-url') || '',
      variants: variants,
      fieldsets: Array.prototype.slice.call(root.querySelectorAll('[data-qv-option]')),
      stage: root.querySelector('[data-qv-stage]'),
      slides: Array.prototype.slice.call(root.querySelectorAll('[data-qv-slide]')),
      dots: Array.prototype.slice.call(root.querySelectorAll('[data-qv-dot]')),
      thumbs: Array.prototype.slice.call(root.querySelectorAll('[data-qv-thumb]')),
      prev: root.querySelector('[data-qv-prev]'),
      next: root.querySelector('[data-qv-next]'),
      index: 0
    };
    initGallery();
    initZoom();
    initShades(root);
    initInfoFade(root);
    updateAvailability();
    updateNav();
  }

  /* hover zoom (mouse only): the image scales around the pointer */
  function initZoom() {
    var c = current;
    if (!c || !c.stage || !fineHover || reduced) return;
    c.stage.addEventListener('pointermove', function (e) {
      var slide = e.target.closest && e.target.closest('[data-qv-slide]');
      if (!slide) return;
      var r = slide.getBoundingClientRect();
      var x = Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100));
      var y = Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100));
      slide.style.setProperty('--zx', x.toFixed(1) + '%');
      slide.style.setProperty('--zy', y.toFixed(1) + '%');
      slide.classList.add('is-zoom');
    });
    c.stage.addEventListener('pointerleave', function () {
      c.slides.forEach(function (s) { s.classList.remove('is-zoom'); });
    });
    c.stage.addEventListener('scroll', function () {
      c.slides.forEach(function (s) { s.classList.remove('is-zoom'); });
    }, { passive: true });
  }

  /* shade row: edge fades from the scroll position */
  function initShades(root) {
    var list = root.querySelector('.mw-qv__shade-list');
    if (!list) return;
    if (list.__mwUpdate) { list.__mwUpdate(); return; }
    var update = function () {
      var more = list.scrollWidth - list.clientWidth > 4;
      list.classList.toggle('is-more-right', more && list.scrollLeft < list.scrollWidth - list.clientWidth - 4);
      list.classList.toggle('is-more-left', more && list.scrollLeft > 4);
    };
    list.__mwUpdate = update;
    var ticking = false;
    list.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; update(); });
    }, { passive: true });
    requestAnimationFrame(update);
  }

  /* details column: bottom fade while there is more to scroll */
  function initInfoFade(root) {
    var scroller = root.querySelector('[data-qv-scroll]');
    var info = root.querySelector('.mw-qv__info');
    if (!scroller || !info) return;
    if (scroller.__mwUpdate) { scroller.__mwUpdate(); return; }
    var update = function () {
      var more = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop > 12 && scroller.clientHeight < scroller.scrollHeight;
      info.classList.toggle('is-more', more && !isPhone());
    };
    scroller.__mwUpdate = update;
    var ticking = false;
    scroller.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; update(); });
    }, { passive: true });
    requestAnimationFrame(update);
    setTimeout(update, 400);
  }

  /* gallery */
  function initGallery() {
    var c = current;
    if (!c.stage || c.slides.length < 2) {
      if (c.prev) c.prev.disabled = true;
      if (c.next) c.next.disabled = true;
      return;
    }
    var ticking = false;
    c.stage.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        var w = c.stage.clientWidth || 1;
        setIndex(Math.round(c.stage.scrollLeft / w), false);
      });
    }, { passive: true });
    if (c.prev) c.prev.addEventListener('click', function () { goTo(c.index - 1); });
    if (c.next) c.next.addEventListener('click', function () { goTo(c.index + 1); });
    c.thumbs.forEach(function (t) {
      t.addEventListener('click', function () { goTo(parseInt(t.getAttribute('data-index'), 10) || 0); });
    });
    c.stage.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(c.index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(c.index - 1); }
    });
    setIndex(0, false);
  }
  function goTo(i) {
    var c = current;
    if (!c || !c.stage) return;
    var n = c.slides.length;
    i = Math.max(0, Math.min(n - 1, i));
    c.stage.scrollTo({ left: i * c.stage.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
    setIndex(i, true);
  }
  function setIndex(i, force) {
    var c = current;
    if (!c) return;
    var n = c.slides.length;
    i = Math.max(0, Math.min(n - 1, i));
    if (c.index === i && !force) return;
    c.index = i;
    c.dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); });
    c.thumbs.forEach(function (t, k) { t.classList.toggle('is-active', k === i); });
    if (c.prev) c.prev.disabled = i === 0;
    if (c.next) c.next.disabled = i === n - 1;
  }

  /* variants */
  function selectedOptions() {
    return current.fieldsets.map(function (fs) {
      var checked = fs.querySelector('[data-qv-radio]:checked');
      return checked ? checked.value : null;
    });
  }
  function findVariant(opts) {
    return current.variants.find(function (v) {
      return v.options.length === opts.length && v.options.every(function (o, k) { return o === opts[k]; });
    }) || null;
  }
  function updateAvailability() {
    var c = current;
    if (!c || !c.fieldsets.length) return;
    var sel = selectedOptions();
    c.fieldsets.forEach(function (fs, idx) {
      fs.querySelectorAll('[data-qv-pill]').forEach(function (pill) {
        var value = pill.getAttribute('data-value');
        var ok = c.variants.some(function (v) {
          if (!v.available) return false;
          return v.options.every(function (o, k) {
            if (k === idx) return o === value;
            return sel[k] === null || o === sel[k];
          });
        });
        pill.classList.toggle('is-unavailable', !ok);
      });
      var label = fs.querySelector('[data-qv-option-value]');
      if (label) label.textContent = sel[idx] || '';
    });
  }
  function onOptionChange() {
    var c = current;
    if (!c) return;
    var sel = selectedOptions();
    var v = findVariant(sel);
    updateAvailability();
    var add = c.root.querySelector('[data-qv-add]');
    var addText = c.root.querySelector('[data-qv-add-text]');
    var input = c.root.querySelector('[data-qv-variant-input]');
    // 26 Oct: no matching variant (an impossible combination) used to hide EVERY price node; now the last
    // price stays visible, and the PDP links drop the stale variant.
    if (v) c.root.querySelectorAll('[data-qv-price]').forEach(function (p) {
      var show = String(p.getAttribute('data-variant-id')) === String(v.id);
      if (show && p.hidden) {
        p.classList.remove('is-updated');
        requestAnimationFrame(function () { p.classList.add('is-updated'); });
      }
      p.hidden = !show;
    });
    if (v) {
      if (input) input.value = v.id;
      var form = c.root.querySelector('form[data-product-form]');
      if (form) form.setAttribute('data-variant-title', v.title || '');
      if (add) add.disabled = !v.available;
      if (addText) addText.textContent = v.available ? (S.addToCart || 'Add to cart') : (S.soldOut || 'Sold out');
      c.root.querySelectorAll('[data-qv-pdp-link]').forEach(function (a) {
        var base = a.getAttribute('href').split('?')[0].split('#')[0];
        a.setAttribute('href', base + '?variant=' + v.id);
      });
      root_setVariantAttr(v.id);
      var skuCell = c.root.querySelector('[data-qv-sku]');
      if (skuCell && v.sku) skuCell.textContent = v.sku;
      if (v.media) {
        var k = c.slides.findIndex(function (s) { return String(s.getAttribute('data-media-id')) === String(v.media); });
        if (k >= 0) goTo(k);
      }
    } else {
      if (add) add.disabled = true;
      if (addText) addText.textContent = S.unavailable || 'Unavailable';
      c.root.querySelectorAll('[data-qv-pdp-link]').forEach(function (a) {
        a.setAttribute('href', a.getAttribute('href').split('?')[0].split('#')[0]);
      });
    }
    announceVariant(v, sel);
  }
  // 26 Oct: screen readers hear the result of an option change (price text is read from DN's node, never written).
  function announceVariant(v, sel) {
    var c = current;
    if (!c) return;
    var parts = [sel.filter(Boolean).join(' / ')];
    if (v) {
      var priceEl = c.root.querySelector('[data-qv-price]:not([hidden])');
      var price = '';
      if (priceEl) {
        var copy = priceEl.cloneNode(true);   // read-only copy: drop the finance line ("From $X/mo with Splitit")
        copy.querySelectorAll('[data-qv-finance], .mw-qv__finance, [class*="splitit"]').forEach(function (n) { n.remove(); });
        price = copy.textContent.replace(/\s+/g, ' ').trim();
      }
      if (price) parts.push(price);
      parts.push(v.available ? (S.available || 'Available') : (S.soldOut || 'Sold out'));
    } else {
      parts.push(S.unavailable || 'Unavailable');
    }
    setStatus(parts.filter(Boolean).join('. '));
  }
  function root_setVariantAttr(id) { if (current && current.root) current.root.setAttribute('data-qv-variant', id); }

  body.addEventListener('change', function (e) {
    if (e.target.matches && e.target.matches('[data-qv-radio]')) onOptionChange();
  });
  body.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-qv-readmore]');
    if (!btn || !current) return;
    var full = current.root.querySelector('[data-qv-desc-full]');
    var brief = current.root.querySelector('[data-qv-desc-short]');
    var text = btn.querySelector('[data-qv-readmore-text]');
    var expanded = btn.getAttribute('aria-expanded') === 'true';
    if (full) full.hidden = expanded;
    if (brief) brief.hidden = !expanded;
    btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    if (text) text.textContent = expanded ? 'Read more' : 'Read less';
    refreshInfoFade();
  });
  body.addEventListener('toggle', function (e) {
    if (e.target.matches && e.target.matches('[data-qv-details]')) refreshInfoFade();
  }, true);
  function refreshInfoFade() {
    var scroller = current && current.root.querySelector('[data-qv-scroll]');
    if (scroller && scroller.__mwUpdate) requestAnimationFrame(scroller.__mwUpdate);
  }

  /* "More shades": load another product into the open panel */
  body.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-qv-load]');
    if (!link) return;
    e.preventDefault();
    var path = link.getAttribute('data-qv-load');
    if (!path) return;
    var seq = ++openSeq;
    body.classList.add('is-swapping');
    body.setAttribute('aria-busy', 'true');
    setStatus(S.loading || 'Loading');
    var info = { path: path.split('?')[0], variant: '', title: link.getAttribute('title') || '' };
    var tStart = performance.now();
    var fetching = load(sectionUrl(info.path, ''));
    afterPaint().then(function () { return fetching; }).then(function (markup) {
      if (seq !== openSeq) return;
      render(markup, info);
      announce('swap', info, performance.now() - tStart);
    }).catch(function () {
      if (seq !== openSeq) return;
      body.classList.remove('is-swapping');
      window.location.href = path;
    });
  });

  /* add-to-cart feedback (the theme's <product-form> does the request) */
  body.addEventListener('submit', function (e) {
    var form = e.target.closest && e.target.closest('form[data-product-form]');
    if (!form) return;
    var btn = form.querySelector('[data-qv-add]');
    if (btn && !btn.disabled) btn.classList.add('is-loading');
  });
  document.addEventListener('theme:cart:error', function () {
    var btn = body.querySelector('[data-qv-add]');
    if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
  });

  window.MilanoQuickview = {
    open: function (path, variant) { open({ path: path, variant: variant || '', title: '' }, null); },
    close: close,
    prefetch: function (path, variant) { prefetch({ path: path, variant: variant || '' }); },
    scan: scan,
    version: '2026-10-26-leftovers'
  };
})();
